import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, FileCheck2, FileSpreadsheet, FileText, History, Loader2, MessageSquarePlus, Package, ReceiptText, Send, ShoppingCart, UserRound } from 'lucide-react';
import { inquiriesAPI, vendorRfqsAPI } from '../../services/api';
import { InquiryDocumentsTab } from '../../components/inquiries/InquiryDocumentsTab';
import { InquiryItemsTab } from '../../components/inquiries/InquiryItemsTab';
import { InquiryStatusControl } from '../../components/inquiries/InquiryStatusControl';
import { VendorRfqDialog } from '../../components/inquiries/VendorRfqDialog';
import { VendorResponseDialog } from '../../components/inquiries/VendorResponseDialog';
import { VendorQuoteUploadDialog } from '../../components/inquiries/VendorQuoteUploadDialog';
import { VendorRfqEmailDialog } from '../../components/inquiries/VendorRfqEmailDialog';
import { InquiryCostings, InquiryQuotations, InquirySalesOrders, InquiryVendorComparison, InquiryVendorRfqs } from '../../components/inquiries/InquiryLinkedRecords';
import { AssignInquiryCustomerDialog } from '../../components/inquiries/AssignInquiryCustomerDialog';
import { LinkQuotationDialog } from '../../components/inquiries/LinkQuotationDialog';
import { InquiryCostingImports } from '../../components/inquiries/InquiryCostingImports';
import { InquiryCustomerPOs } from '../../components/inquiries/InquiryCustomerPOs';
import { InquiryCommercialTimeline } from '../../components/inquiries/InquiryCommercialTimeline';
import { InquiryCycleTimeCard } from '../../components/inquiries/InquiryCycleTimeCard';
import { InquiryPeoplePanel } from '../../components/inquiries/InquiryPeoplePanel';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { STATUS_STYLES, statusLabel } from './constants';

const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-IN') : '—';

export default function InquiryWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [responseRfq, setResponseRfq] = useState(null);
  const [quoteUploadRfq, setQuoteUploadRfq] = useState(null);
  const [emailRfq, setEmailRfq] = useState(null);
  const [note, setNote] = useState('');
  const [comparison, setComparison] = useState([]);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [linkQuotationOpen, setLinkQuotationOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, comparisonResponse] = await Promise.all([
        inquiriesAPI.get(id), inquiriesAPI.vendorComparison(id),
      ]);
      setInquiry(detail.data);
      setComparison(comparisonResponse.data.items || []);
      return detail.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load inquiry');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (payload) => {
    setSaving(true);
    try { await inquiriesAPI.updateStatus(id, payload); toast.success('Inquiry status updated'); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Status update failed'); }
    finally { setSaving(false); }
  };

  const assignCustomer = async (customerId) => {
    setSaving(true);
    try { await inquiriesAPI.update(id, { customer_id: customerId }); setCustomerDialogOpen(false); toast.success('Customer assigned'); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Customer assignment failed'); }
    finally { setSaving(false); }
  };

  const createQuote = () => {
    if (!selectedIds.length) return;
    navigate(`/quotations/new?inquiry=${id}&items=${selectedIds.join(',')}`);
  };

  const unlinkQuotation = async (record) => {
    try {
      const response = await inquiriesAPI.unlinkQuotation(id, record.id);
      toast.success(response.data.message);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not unlink this quotation');
    }
  };

  const createRfq = async (payload) => {
    setSaving(true);
    try {
      const response = await inquiriesAPI.createVendorRfq(id, { ...payload, inquiry_product_ids: selectedIds });
      toast.success(`Vendor RFQ ${response.data.rfq_number} created`);
      setRfqOpen(false); await load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Vendor RFQ creation failed'); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await inquiriesAPI.addNote(id, note); setNote(''); toast.success('Activity note added'); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not add note'); }
    finally { setSaving(false); }
  };

  const saveVendorResponse = async (payload) => {
    setSaving(true);
    try { await vendorRfqsAPI.addResponse(responseRfq.id, payload); setResponseRfq(null); toast.success('Vendor pricing recorded'); await load(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Could not record pricing'); }
    finally { setSaving(false); }
  };

  const downloadExcel = async (rfq) => {
    try {
      const response = await vendorRfqsAPI.downloadExcel(rfq.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${rfq.rfq_number}.xlsx`; anchor.click(); URL.revokeObjectURL(url);
    } catch (error) { toast.error('Excel download failed'); }
  };

  if (loading) return <div className="flex min-h-[420px] items-center justify-center" data-testid="inquiry-workspace-loading"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!inquiry) return <div className="py-20 text-center" data-testid="inquiry-workspace-not-found">Inquiry not found.</div>;

  return (
    <div className="space-y-6 overflow-x-clip animate-fade-in" data-testid="inquiry-workspace-page">
      <header className="sticky top-16 z-20 -mx-4 border-b bg-background/95 px-4 pb-5 pt-2 backdrop-blur md:top-0 md:-mx-8 md:px-8">
        <Button variant="ghost" className="mb-3 px-0" onClick={() => navigate('/inquiries')} data-testid="inquiry-workspace-back-button"><ArrowLeft className="mr-2 h-4 w-4" /> Inquiry register</Button>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2"><div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-3xl font-bold sm:text-4xl" data-testid="inquiry-workspace-number">{inquiry.inquiry_number}</h1>
            <Badge className={STATUS_STYLES[inquiry.status]} data-testid="inquiry-workspace-status">{statusLabel(inquiry.status)}</Badge>
          </div><div className="flex items-center gap-2"><p className="text-lg font-medium" data-testid="inquiry-workspace-customer">{inquiry.customer_name}</p>
            <Button size="sm" variant="ghost" onClick={() => setCustomerDialogOpen(true)} data-testid="assign-inquiry-customer-button">{inquiry.customer_id ? 'Change' : 'Assign customer'}</Button></div></div>
          <InquiryStatusControl key={inquiry.status} currentStatus={inquiry.status} onUpdate={updateStatus} saving={saving} />
        </div>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="inquiry-readiness-strip">
        {[
          { key: 'documents', label: 'Documents', done: (inquiry.documents || []).length > 0, detail: `${(inquiry.documents || []).length} file(s)` },
          { key: 'items', label: 'Items', done: (inquiry.products || []).length > 0, detail: `${(inquiry.products || []).length} item(s)` },
          { key: 'feasibility', label: 'Feasibility', done: Boolean(inquiry.feasibility_review?.verdict), detail: inquiry.feasibility_review?.verdict?.replaceAll('_', ' ') || 'pending' },
          { key: 'costing', label: 'Costing', done: (inquiry.products || []).length > 0 && (inquiry.products || []).every((item) => item.costing_state === 'approved'), detail: `${(inquiry.products || []).filter((item) => item.costing_state === 'approved').length}/${(inquiry.products || []).length} approved · ${(inquiry.costings || []).length} costing(s)` },
          { key: 'sourcing', label: 'Bought-out RFQ', done: (inquiry.vendor_rfqs || []).length > 0, detail: `${(inquiry.vendor_rfqs || []).length} RFQ(s)` },
          { key: 'quotation', label: 'Quotation', done: (inquiry.quotations || []).length > 0, detail: `${(inquiry.quotations || []).length} quote(s)` },
          { key: 'customer-po', label: 'Customer PO', done: (inquiry.customer_pos || []).length > 0, detail: `${(inquiry.customer_pos || []).length} PO(s)` },
          { key: 'sales-order', label: 'Sales order', done: (inquiry.sales_orders || []).length > 0, detail: `${(inquiry.sales_orders || []).length} order(s)` },
        ].map((step) => (
          <div key={step.key} className={`flex items-center gap-2 border px-3 py-2 text-xs ${step.done ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'bg-white text-muted-foreground'}`}
            data-testid={`readiness-${step.key}`}>
            <span className={`h-2 w-2 rounded-full ${step.done ? 'bg-emerald-600' : 'bg-slate-300'}`} />
            <span className="font-medium">{step.label}</span>
            <span>· {step.detail}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="space-y-5">
          <section className="border bg-white p-5" data-testid="inquiry-metadata-panel">
            <h2 className="mb-4 font-heading font-bold">Inquiry details</h2>
            <dl className="space-y-4 text-sm">
              <div><dt className="text-muted-foreground">Customer reference</dt><dd className="font-mono" data-testid="inquiry-customer-reference">{inquiry.customer_inquiry_number || '—'}</dd></div>
              <div className="flex gap-3"><CalendarDays className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><dt className="text-muted-foreground">Received</dt><dd>{formatDate(inquiry.customer_inquiry_date || inquiry.created_at)}</dd></div></div>
              <div className="flex gap-3"><UserRound className="mt-0.5 h-4 w-4 text-muted-foreground" /><div><dt className="text-muted-foreground">Contact</dt><dd>{inquiry.customer_contact_person || '—'}</dd><dd className="break-all text-muted-foreground">{inquiry.customer_contact_email}</dd></div></div>
              <div><dt className="text-muted-foreground">Items</dt><dd data-testid="inquiry-item-count">{inquiry.products?.length || 0}</dd></div>
              <div><dt className="text-muted-foreground">Documents</dt><dd data-testid="inquiry-document-count">{inquiry.documents?.length || 0}</dd></div>
              {inquiry.closure_reason && <div className="border-l-2 border-red-500 pl-3"><dt className="text-muted-foreground">Closure reason</dt><dd data-testid="inquiry-closure-reason">{inquiry.closure_reason.replaceAll('_', ' ')}</dd><dd className="text-muted-foreground">{inquiry.closure_notes}</dd></div>}
            </dl>
          </section>
          <section className="border bg-white p-5" data-testid="inquiry-activity-panel">
            <div className="mb-4 flex items-center gap-2"><History className="h-4 w-4 text-blue-600" /><h2 className="font-heading font-bold">Activity</h2></div>
            <div className="mb-4 flex gap-2"><Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add a follow-up note" data-testid="inquiry-activity-note-input" />
              <Button size="icon" onClick={addNote} disabled={!note.trim() || saving} data-testid="inquiry-add-note-button"><MessageSquarePlus className="h-4 w-4" /></Button></div>
            <div className="max-h-[440px] space-y-4 overflow-y-auto">{(inquiry.activities || []).map((activity) => (
              <div key={activity.id} className="border-l-2 border-slate-200 pl-3" data-testid={`inquiry-activity-${activity.id}`}>
                <p className="text-sm font-medium">{activity.description}</p><p className="text-xs text-muted-foreground">{activity.created_by_name} · {new Date(activity.created_at).toLocaleString('en-IN')}</p>
              </div>
            ))}</div>
          </section>
        </aside>

        <main className="min-w-0 border bg-white p-4 sm:p-6">
          <Tabs defaultValue="items">
            <TabsList className="mb-6 h-auto w-full justify-start overflow-x-auto bg-slate-100 p-1">
              <TabsTrigger value="items" data-testid="inquiry-items-tab-trigger"><Package className="mr-2 h-4 w-4" />Items & costing</TabsTrigger>
              <TabsTrigger value="documents" data-testid="inquiry-documents-tab-trigger"><FileText className="mr-2 h-4 w-4" />Documents</TabsTrigger>
              <TabsTrigger value="imports" data-testid="inquiry-costing-imports-tab-trigger"><FileSpreadsheet className="mr-2 h-4 w-4" />Costing imports ({inquiry.costing_imports?.length || 0})</TabsTrigger>
              <TabsTrigger value="costings" data-testid="inquiry-costings-tab-trigger">Costings ({inquiry.costings?.length || 0})</TabsTrigger>
              <TabsTrigger value="quotations" data-testid="inquiry-quotations-tab-trigger"><ReceiptText className="mr-2 h-4 w-4" />Quotations ({inquiry.quotations?.length || 0})</TabsTrigger>
              <TabsTrigger value="vendors" data-testid="inquiry-vendor-rfqs-tab-trigger"><Send className="mr-2 h-4 w-4" />Vendor RFQs ({inquiry.vendor_rfqs?.length || 0})</TabsTrigger>
              <TabsTrigger value="comparison" data-testid="inquiry-vendor-comparison-tab-trigger">Comparison</TabsTrigger>
              <TabsTrigger value="customer-pos" data-testid="inquiry-customer-pos-tab-trigger"><FileCheck2 className="mr-2 h-4 w-4" />Customer POs ({inquiry.customer_pos?.length || 0})</TabsTrigger>
              <TabsTrigger value="sales-orders" data-testid="inquiry-sales-orders-tab-trigger"><ShoppingCart className="mr-2 h-4 w-4" />Sales orders ({inquiry.sales_orders?.length || 0})</TabsTrigger>
              <TabsTrigger value="history" data-testid="inquiry-history-tab-trigger"><History className="mr-2 h-4 w-4" />History</TabsTrigger>
            </TabsList>
            <TabsContent value="items"><InquiryItemsTab inquiry={inquiry} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onQuote={createQuote} onVendorRfq={() => setRfqOpen(true)} onRefresh={load} /></TabsContent>
            <TabsContent value="documents"><InquiryDocumentsTab inquiryId={id} inquiry={inquiry} documents={inquiry.documents || []} onRefresh={load} /></TabsContent>
            <TabsContent value="imports"><InquiryCostingImports inquiry={inquiry} onRefresh={load} /></TabsContent>
            <TabsContent value="costings"><InquiryCostings records={inquiry.costings || []} /></TabsContent>
            <TabsContent value="quotations">
              <InquiryQuotations
                records={inquiry.quotations || []}
                onLinkExisting={() => setLinkQuotationOpen(true)}
                onUnlink={unlinkQuotation}
              />
            </TabsContent>
            <TabsContent value="vendors"><InquiryVendorRfqs records={inquiry.vendor_rfqs || []} onExcel={downloadExcel} onResponse={setResponseRfq} onEmail={setEmailRfq} onUploadQuote={setQuoteUploadRfq} /></TabsContent>
            <TabsContent value="comparison"><InquiryVendorComparison comparison={comparison} /></TabsContent>
            <TabsContent value="customer-pos"><InquiryCustomerPOs inquiry={inquiry} onRefresh={load} /></TabsContent>
            <TabsContent value="sales-orders"><InquirySalesOrders records={inquiry.sales_orders || []} /></TabsContent>
            <TabsContent value="history"><InquiryCommercialTimeline records={inquiry.commercial_history || []} /></TabsContent>
          </Tabs>
        </main>
      </div>

      <InquiryPeoplePanel inquiryId={id} />

      <InquiryCycleTimeCard inquiryId={id} />

      <VendorRfqDialog open={rfqOpen} onOpenChange={setRfqOpen} selectedCount={selectedIds.length} onCreate={createRfq} saving={saving} />
      <VendorResponseDialog rfq={responseRfq} open={Boolean(responseRfq)} onOpenChange={(open) => !open && setResponseRfq(null)} onSave={saveVendorResponse} saving={saving} />
      <VendorQuoteUploadDialog rfq={quoteUploadRfq} open={Boolean(quoteUploadRfq)} onOpenChange={(open) => !open && setQuoteUploadRfq(null)} onApplied={load} />
      <VendorRfqEmailDialog rfq={emailRfq} open={Boolean(emailRfq)} onOpenChange={(open) => !open && setEmailRfq(null)} onSent={load} />
      <AssignInquiryCustomerDialog open={customerDialogOpen} onOpenChange={setCustomerDialogOpen} currentCustomerId={inquiry.customer_id} onAssign={assignCustomer} saving={saving} />
      <LinkQuotationDialog inquiryId={id} open={linkQuotationOpen} onOpenChange={setLinkQuotationOpen} onLinked={load} />
    </div>
  );
}